import styled, { css } from 'styled-components';
import type { TextAreaProps } from './field.type';
import {
  accent50,
  borderRadius,
  default700,
  fontSizeBodyM,
  fontSizeLabelSM,
  fontSizeLabelsRegular,
  fontSizeSubHeading,
  fontWeightRegular,
  fontWeightSemibold,
  neutral0,
  neutral200,
  neutral500,
  neutral600,
  primary700,
  spacing1,
  spacing2,
  spacing3,
  spacing4,
  spacing5,
  spacing6,
  spacing9,
} from '../../design-tokens';
import { Icon as BaseIcon } from '../Icons';
import { BodyText, commonBody, fontSize2 } from 'lib/design-system';
import type { ReactNode } from 'react';

const borderBase = css`
  border: 1px solid ${neutral200};
  border-radius: ${borderRadius()};

  :hover {
    border-color: ${neutral500};
  }

  :focus-within {
    border-color: ${default700};
  }
`;

const subText = css`
  font-size: ${fontSizeLabelsRegular};
  line-height: ${fontSizeSubHeading};
  font-weight: ${fontWeightRegular};
  margin-top: ${spacing2};
`;

const timing = '.2s';
const inputComponentHeight = '52px';
const inputComponentVerticalPadding = spacing1;
const inputComponentVerticalBorder = '1px';
const inputLabelFilledLineHeight = '12px';

export const InputGroup = styled.div<
  StyledDiv & {
    withIcon?: boolean;
    showError?: boolean;
    disabled?: boolean;
    disableLeftBorder?: boolean;
  }
>`
  ${borderBase};
  background-color: ${neutral0};
  height: ${inputComponentHeight};
  display: flex;
  flex-direction: row;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: ${inputComponentVerticalPadding} ${spacing3};
  line-height: ${spacing5};
  gap: 0 ${spacing2};

  :focus-within {
    ${({ disableLeftBorder }) =>
      !disableLeftBorder &&
      `padding-left: calc(${spacing2} + 1px); border-left-width: ${spacing1};`}
  }
  ${({ withIcon }) =>
    withIcon &&
    css`
      padding: ${spacing2} ${spacing3} ${spacing2} 37px;
    `}
  ${({ showError }) =>
    showError &&
    css`
      padding-left: calc(${spacing2} + 1px);
      border-color: ${primary700} !important;
      border-left-width: 4px;
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      background: ${neutral200};
      border-color: ${neutral200} !important;
    `}
    ${BaseIcon};
`;

export const ErrorMessage = styled.span.attrs<{ role: string }>({
  role: 'alert',
})<StyledSpan>`
  ${subText};
  color: ${primary700};
`;

export const LengthInfoContainer = styled.div<StyledDiv>`
  ${subText};
  color: ${neutral600};
  display: flex;
  justify-content: space-between;
`;

export const InputField = styled.input<
  StyledInput & {
    filled: boolean;
    hasLabel: boolean;
  }
>`
  ${commonBody};
  font-size: ${fontSize2};
  height: calc(
    ${inputComponentHeight} - ${inputComponentVerticalPadding} -
      ${inputComponentVerticalPadding} - ${inputComponentVerticalBorder} -
      ${inputComponentVerticalBorder}
  );
  border: none;
  outline: none;
  flex-grow: 1;

  ${({ hasLabel }) =>
    hasLabel &&
    css`
      align-self: flex-end;
    `}

  ${({ hasLabel, filled }) =>
    hasLabel &&
    filled &&
    css`
      height: calc(
        ${inputComponentHeight} - ${inputComponentVerticalPadding} -
          ${inputComponentVerticalPadding} - ${inputLabelFilledLineHeight} -
          ${inputComponentVerticalBorder} - ${inputComponentVerticalBorder}
      );
    `}

  &::placeholder {
    color: ${neutral600};
  }

  :disabled {
    background: ${neutral200};
  }
`;

export const InputWrapper = styled.div<StyledDiv>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

export const TextArea = styled(InputField)<TextAreaProps>`
  min-height: 150px;
  resize: none;
`;

export const TextAreaInputGroup = styled(InputGroup)`
  height: unset;
`;

export const Label = styled.label.attrs<{
  filled: boolean;
  withIcon: boolean;
}>(({ filled, withIcon }) => {
  const classes: string[] = [];
  if (filled) {
    classes.push('filled');
  }

  if (withIcon) {
    classes.push('with-icon');
  }

  return {
    className: classes.join(' '),
  };
})<
  { children?: ReactNode; className?: string; htmlFor?: string } & {
    filled: boolean;
    withIcon: boolean;
  }
>`
  position: absolute;
  left: 13px;
  font-size: ${fontSizeBodyM};
  line-height: ${spacing6};
  letter-spacing: 0;
  font-weight: ${fontWeightRegular};
  color: ${neutral600};
  transition:
    font-size ${timing} ease,
    top ${timing} ease,
    color ${timing} ease;

  &.filled {
    top: calc(${spacing2} + 1px); // Spacing + border
    font-size: ${fontSizeLabelSM};
    line-height: ${inputLabelFilledLineHeight};
  }
  &.with-icon {
    left: ${spacing9};
  }
`;

export const CheckboxOrRadioLabel = styled(BodyText)<{ noBorder?: boolean }>`
  display: flex;
  gap: ${spacing4};
  cursor: pointer;
  font-weight: ${fontWeightSemibold};
  background: ${neutral0};
  ${({ noBorder }) =>
    noBorder
      ? css`
          border: none;
          margin: 0;
          padding: 0;
        `
      : css`
          ${borderBase};
          margin: ${spacing4} 0;
          padding: ${spacing4};
        `}
`;

export const CheckboxInput = styled.input.attrs({
  'aria-invalid': false,
})<
  StyledInput & {
    'aria-invalid'?: boolean;
  }
>`
  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;
  min-width: 16px;
  min-height: 16px;
  width: 16px;
  height: 16px;
  margin: ${spacing1};

  appearance: none;
  accent-color: ${default700};
  background-color: ${neutral0};
  border: solid 1px ${neutral600};
  border-radius: ${borderRadius(0.5)};

  vertical-align: bottom;

  transition: background-color 0.1s ease-in-out;

  cursor: pointer;

  :checked {
    background-color: ${default700};
    border: solid 1px ${default700};
    :after {
      font-size: ${fontSizeLabelSM};
      color: white;
      content: '✔';
    }
  }

  ${({ ['aria-invalid']: invalid }) =>
    // HACK - Was unable to get aria-invalid to work as an attribute selector for some reason
    invalid &&
    css`
      border: solid 1px ${primary700};

      :checked {
        background-color: ${primary700};
      }
    `}
`;

export const RefreshedCheckboxInput = styled.input<{
  'aria-invalid'?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;
  min-width: 16px;
  min-height: 16px;
  width: 16px;
  height: 16px;
  margin: ${spacing1};

  accent-color: ${accent50};
  background-color: ${neutral0};
  border: solid 1px ${neutral600};
  border-radius: ${borderRadius(0.5)};

  vertical-align: bottom;

  transition: background-color 0.1s ease-in-out;

  cursor: pointer;

  :focus-visible {
    border-color: ${accent50};
  }

  :checked {
    background-color: ${default700};
    border: solid 1px ${default700};
    :after {
      font-size: ${fontSizeLabelSM};
      color: white;
    }
  }

  ${({ ['aria-invalid']: invalid }) =>
    // HACK - Was unable to get aria-invalid to work as an attribute selector for some reason
    invalid &&
    css`
      border: solid 1px ${primary700};

      :checked {
        background-color: ${primary700};
      }
    `}
`;

export const RadioInput = styled(CheckboxInput)`
  border-radius: 50%;

  :checked {
    background-color: ${neutral0};
    :after {
      content: ' ';
      border: 4px solid ${default700};
      border-radius: 50%;
    }
  }
`;
